/**
 * The yield statement of one harvest as an A4 sheet to print and sign (owner, 2026-09-27: "Yield
 * Statement summary? (some are based on tonnage, some are based on M3 - log wise)"): the logs with
 * their length, mid-girth and volume, or the loads with their weighbridge weights, and the totals.
 */
export interface YieldStatementInput {
  organisation: string;
  harvestCode: string;
  harvestDate: string;
  farmer: string;
  plot: string;
  species: string;
  trees: number;
  basis: 'log_volume' | 'weight';
  formula: 'hoppus' | 'true_mid_girth';
  lines: Array<Record<string, unknown>>;
}

const escape = (text: unknown) => String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
const num = (value: unknown) => (value === null || value === undefined || value === '' ? null : Number(value));
const fixed = (value: number | null, digits: number) => (value === null || Number.isNaN(value) ? '' : value.toFixed(digits));

export function yieldTotals(input: Pick<YieldStatementInput, 'basis' | 'lines'>) {
  if (input.basis === 'log_volume') {
    const logs = input.lines.reduce((sum, line) => sum + (num(line.pieces) ?? 1), 0);
    const volume = input.lines.reduce((sum, line) => sum + (num(line.volume_m3) ?? 0), 0);
    return { logs, volume: Math.round(volume * 10000) / 10000, weight: null as number | null };
  }
  const weight = input.lines.reduce((sum, line) => sum + (num(line.net_t) ?? ((num(line.gross_t) ?? 0) - (num(line.tare_t) ?? 0))), 0);
  return { logs: null as number | null, volume: null as number | null, weight: Math.round(weight * 1000) / 1000 };
}

export function buildYieldStatementHtml(input: YieldStatementInput): string {
  const totals = yieldTotals(input);
  const logs = input.basis === 'log_volume';
  const head = logs
    ? '<tr><th>#</th><th>Log no.</th><th class="num">Length (m)</th><th class="num">Mid-girth (cm)</th><th class="num">Pieces</th><th class="num">Volume (m³)</th></tr>'
    : '<tr><th>#</th><th>Weighbridge ticket</th><th>Vehicle</th><th class="num">Gross (t)</th><th class="num">Tare (t)</th><th class="num">Net (t)</th></tr>';
  const body = input.lines.map((line, at) => (logs
    ? `<tr><td>${at + 1}</td><td>${escape(line.log_no)}</td><td class="num">${fixed(num(line.length_m), 2)}</td><td class="num">${fixed(num(line.girth_cm), 1)}</td>`
      + `<td class="num">${num(line.pieces) ?? 1}</td><td class="num">${fixed(num(line.volume_m3), 4)}</td></tr>`
    : `<tr><td>${at + 1}</td><td>${escape(line.ticket_no)}</td><td>${escape(line.vehicle_no)}</td><td class="num">${fixed(num(line.gross_t), 3)}</td>`
      + `<td class="num">${fixed(num(line.tare_t) ?? 0, 3)}</td><td class="num">${fixed(num(line.net_t) ?? ((num(line.gross_t) ?? 0) - (num(line.tare_t) ?? 0)), 3)}</td></tr>`)).join('');
  const foot = logs
    ? `<tr><th colspan="4">Total</th><th class="num">${totals.logs}</th><th class="num">${fixed(totals.volume, 4)}</th></tr>`
    : `<tr><th colspan="5">Total</th><th class="num">${fixed(totals.weight, 3)}</th></tr>`;
  const perTree = input.trees > 0
    ? logs ? `${fixed((totals.volume ?? 0) / input.trees, 3)} m³ per tree` : `${fixed((totals.weight ?? 0) / input.trees, 3)} t per tree` : '';
  const formula = input.formula === 'true_mid_girth' ? 'true volume, g² × L / 4π' : 'quarter-girth (Hoppus), (g/4)² × L';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Yield statement ${escape(input.harvestCode)}</title>
<style>
  body { font-family: "Noto Sans", Arial, sans-serif; color: #111; margin: 16px auto; background: #e5e7eb; font-size: 13px; }
  .page { width: 794px; min-height: 1123px; margin: 0 auto; padding: 40px 44px; background: #fff; box-sizing: border-box; }
  h1 { font-size: 18px; margin: 0 0 2px; text-align: center; }
  .sub { text-align: center; color: #555; margin: 0 0 18px; }
  table { width: 100%; border-collapse: collapse; }
  .meta th, .meta td { text-align: left; padding: 3px 6px; font-weight: normal; }
  .meta th { color: #555; width: 30%; }
  .lines th, .lines td { border: 1px solid #999; padding: 5px 8px; }
  .lines thead th { background: #f2f2f2; font-size: 12px; }
  .lines tfoot th { background: #f9fafb; }
  .num { text-align: right; }
  .note { color: #555; margin: 10px 0 0; }
  .sign { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; margin-top: 56px; }
  .sign div { border-top: 1px solid #333; padding-top: 6px; text-align: center; color: #333; }
  @media print { body { margin: 0; background: #fff; } .page { padding: 12mm; } }
</style></head><body><section class="page">
<h1>YIELD STATEMENT</h1>
<p class="sub">${escape(input.organisation)} &middot; harvest ${escape(input.harvestCode)}</p>
<table class="meta" style="margin-bottom:12px">
  <tr><th>Farmer</th><td>${escape(input.farmer)}</td></tr>
  <tr><th>Plot</th><td>${escape(input.plot)}</td></tr>
  <tr><th>Species</th><td>${escape(input.species)}</td></tr>
  <tr><th>Harvested on</th><td>${escape(input.harvestDate)}</td></tr>
  <tr><th>Trees felled</th><td>${input.trees}</td></tr>
  <tr><th>Measured</th><td>${logs ? `log by log, ${formula}` : 'by weight on a weighbridge (gross less tare)'}</td></tr>
</table>
<table class="lines"><thead>${head}</thead><tbody>${body}</tbody><tfoot>${foot}</tfoot></table>
<p class="note">${perTree}</p>
<div class="sign"><div>Measured by</div><div>Farmer</div><div>Buyer / receiver</div></div>
</section></body></html>`;
}
